import { ROUTES } from 'Constants';
import { appConfigStore } from 'Models/AppConfigStore';
import { bookingTypeOptions } from 'Models/Enums';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import classNames from 'classnames';
import { SearchResultOption } from 'Views/Components/_HumanWritten/Search/SearchResultOption';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { BookingDto, CharterSearchDto, UserDto, userSearchFetch } from 'Util/_HumanWritten/UserSearchFetch';
import GuestTag from '../Tag/GuestTag';

// The shortest term the server searches (SearchUsersQueryBuilder.MinimumSearchLength). Shorter
// terms cannot use the search indexes, so they read every searched table in full; normalised the
// same way the server does — trimmed, '#' removed — so the two agree on what counts.
const MIN_SEARCH_LENGTH = 3;
const normaliseSearchTerm = (text: string) => text.trim().replace(/#/g, '');

export function Search() {
	const searchRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [hideInput, setHideInput] = useState<boolean>(true);
	const [searchText, setSearchText] = useState<string>('');
	const [users, setUsers] = useState<UserDto[] | null>(null);
	const [bookings, setBookings] = useState<BookingDto[] | null>(null);
	const [userTotalCount, setUserTotalCount] = useState<number>(0);
	const [usersHasMore, setUsersHasMore] = useState<boolean>(false);
	const [bookingTotalCount, setBookingTotalCount] = useState<number>(0);
	const [parkingResults, setParkingResults] = useState<BookingDto[] | null>(null);
	const [parkingTotalCount, setParkingTotalCount] = useState<number>(0);
	const [charterResults, setCharterResults] = useState<CharterSearchDto[] | null>(null);
	const [charterTotalCount, setCharterTotalCount] = useState<number>(0);
	// Per-panel loading — users finishes when the fast event arrives (independent of the
	// optional slower users-exact-count follow-up); the bookings UI panel covers the
	// bookings + parking + charters sub-queries so it stays "loading" until all have settled.
	const [usersLoading, setUsersLoading] = useState<boolean>(false);
	const [bookingsLoading, setBookingsLoading] = useState<boolean>(false);

	// Holds the AbortController for the in-flight stream; aborted on next keystroke (so
	// the server cancels the parallel sub-queries) and on unmount / close.
	const abortRef = useRef<AbortController | null>(null);

	const searchUsers = async (text: string) => {
		abortRef.current?.abort();

		if (!text || normaliseSearchTerm(text).length < MIN_SEARCH_LENGTH) {
			setUsers(null);
			setBookings(null);
			setParkingResults(null);
			setCharterResults(null);
			setUserTotalCount(0);
			setBookingTotalCount(0);
			setParkingTotalCount(0);
			setCharterTotalCount(0);
			setUsersHasMore(false);
			setUsersLoading(false);
			setBookingsLoading(false);
			return;
		}

		const controller = new AbortController();
		abortRef.current = controller;

		setUsers(null);
		setBookings(null);
		setParkingResults(null);
		setCharterResults(null);
		setUserTotalCount(0);
		setBookingTotalCount(0);
		setParkingTotalCount(0);
		setCharterTotalCount(0);
		setUsersHasMore(false);
		setUsersLoading(true);
		setBookingsLoading(true);

		// The one panel is fed by three producers, so it stops spinning only once all three have
		// landed — otherwise the first to arrive would clear the spinner and the panel would appear
		// to be missing the results still on their way.
		let bookingsArrived = false;
		let parkingArrived = false;
		let chartersArrived = false;

		try {
			// eslint-disable-next-line no-restricted-syntax
			for await (const evt of userSearchFetch(text, controller.signal)) {
				switch (evt.type) {
					case 'users':
						setUsers(evt.items);
						setUserTotalCount(evt.count);
						setUsersHasMore(evt.hasMore);
						setUsersLoading(false);
						break;
					case 'users-exact-count':
						setUserTotalCount(evt.count);
						setUsersHasMore(false);
						break;
					case 'bookings':
						setBookings(evt.items);
						setBookingTotalCount(evt.count);
						bookingsArrived = true;
						if (parkingArrived && chartersArrived) {
							setBookingsLoading(false);
						}
						break;
					case 'charters':
						setCharterResults(evt.items);
						setCharterTotalCount(evt.count);
						chartersArrived = true;
						if (bookingsArrived && parkingArrived) {
							setBookingsLoading(false);
						}
						break;
					case 'parking':
						setParkingResults(evt.items);
						setParkingTotalCount(evt.count);
						parkingArrived = true;
						if (bookingsArrived && chartersArrived) {
							setBookingsLoading(false);
						}
						break;
					default:
						break;
				}
			}
			// Stream ended — clear any spinners still up (e.g. if a producer failed
			// silently and didn't emit, we don't want to spin forever).
			setUsersLoading(false);
			setBookingsLoading(false);
		} catch (err: unknown) {
			const name = (err as { name?: string })?.name;
			if (name !== 'AbortError') {
				setUsers(prev => prev ?? []);
				setBookings(prev => prev ?? []);
				setParkingResults(prev => prev ?? []);
				setCharterResults(prev => prev ?? []);
				setUsersLoading(false);
				setBookingsLoading(false);
			}
		}
	};

	const debouncedUserSearch = React.useCallback(AwesomeDebouncePromise(searchUsers, 500), []);

	useEffect(() => {
		debouncedUserSearch(searchText);
	}, [searchText]);

	// Abort any in-flight stream when the component unmounts so the server cancels its
	// sub-queries.
	useEffect(() => () => abortRef.current?.abort(), []);

	// Focus input when opened
	useEffect(() => {
		if (!hideInput) {
			inputRef.current?.focus();
		}
	}, [hideInput]);

	// Close search when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
				closeSearch();
			}
		};

		if (!hideInput) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [hideInput]);

	const closeSearch = () => {
		abortRef.current?.abort();
		setHideInput(true);
		setSearchText('');
		setUsers(null);
		setBookings(null);
		setParkingResults(null);
		setCharterResults(null);
		setUsersLoading(false);
		setBookingsLoading(false);
	};

	const searchTooShort = searchText.trim() !== '' && normaliseSearchTerm(searchText).length < MIN_SEARCH_LENGTH;
	const tooShortMessage = `Type at least ${MIN_SEARCH_LENGTH} characters to search`;

	const userRender: ReactNode[] = [];
	const bookingRender: ReactNode[] = [];
	if (users !== null && users.length > 0) {
		users.forEach(user => {
			// Staff-UI display mode: show the company name in place of the personal name when enabled
			// and the user has one. Falls back to the personal name otherwise.
			const trimmedCompany = user.companyName?.trim();
			const displayName =
				appConfigStore.displayCompanyNameInsteadOfName && trimmedCompany ? trimmedCompany : user.name;
			userRender.push(
				<SearchResultOption
					key={user.id}
					iconName="person"
					linkUrl={ROUTES.USER_DETAIL.url(user.id)}
					label={
						<>
							<span className="search-nav-bar__result-name">{displayName}</span>
							{user.isGuest && <GuestTag />}
						</>
					}
					description={user.email}
					onClick={closeSearch}
					testId="search-result-user"
				/>,
			);
		});
	}
	if (bookings !== null && bookings.length > 0) {
		bookings.forEach(booking => {
			bookingRender.push(
				<SearchResultOption
					key={booking.id}
					linkUrl={`/bookings/${booking.id}`}
					iconName="book"
					label={`#${booking.humanReadableId}`}
					description={
						bookingTypeOptions[
							booking.bookingType === 'VEHICLE_AND_PASSENGER' ? 'VEHICLE' : booking.bookingType
						]
					}
					onClick={closeSearch}
					testId="search-result-booking"
				/>,
			);
		});
	}
	if (charterResults !== null && charterResults.length > 0) {
		charterResults.forEach(charter => {
			// A charter opens at the customer's view of it, which is where the charters table and the
			// transactions table both send a reader — the staff sidebar lives over the schedule and
			// takes the reader off whatever they were doing.
			bookingRender.push(
				<SearchResultOption
					key={`charter-${charter.id}`}
					linkUrl={ROUTES.CUSTOMER_CHARTER.url(charter.id)}
					iconName="sailing"
					useMaterialIcon
					label={`#${charter.humanReadableId ?? ''}`}
					// What it is called and which ferry, because a reference alone does not tell two
					// apart to somebody who knows the charter by name. A charter can be saved without a
					// name, so the word stands in for one.
					description={[`Charter${charter.status === 'Cancelled' ? ' (cancelled)' : ''}`, charter.name?.trim(), charter.ferryName]
						.filter(Boolean)
						.join(' · ')}
					onClick={closeSearch}
					testId="search-result-charter"
				/>,
			);
		});
	}
	if (parkingResults !== null && parkingResults.length > 0) {
		parkingResults.forEach(parking => {
			bookingRender.push(
				<SearchResultOption
					key={`parking-${parking.id}`}
					linkUrl={`/parking-reservations/${parking.id}`}
					iconName="local_parking"
					useMaterialIcon
					label={`#${parking.humanReadableId}`}
					description="Parking"
					onClick={closeSearch}
				/>,
			);
		});
	}

	return (
		<div
			ref={searchRef}
			className={classNames('search-nav-bar', { 'show-input': !hideInput })}
			onClick={event => {
				event.stopPropagation();
				event.preventDefault();
			}}
		>
			{hideInput ? (
				<button
					type="button"
					className="search-nav-bar__search-btn"
					data-test-id="global-search-trigger"
					onClick={event => {
						event.stopPropagation();
						event.preventDefault();
						setHideInput(false);
					}}
				>
					<Icon name="search" useMaterialIcons />
				</button>
			) : (
				<div className="search-nav-bar__input-wrapper">
					<div className="search-nav-bar__input-container custom-input">
						<Icon name="search" useMaterialIcons classname="search-nav-bar__icon-search" />
						<input
							ref={inputRef}
							type="text"
							className="search-nav-bar__text-input"
							placeholder="Search"
							value={searchText}
							onChange={e => setSearchText(e.target.value)}
							data-test-id="global-search-input"
						/>
						<button
							type="button"
							className="search-nav-bar__icon-clear"
							onClick={event => {
								event.stopPropagation();
								event.preventDefault();
								closeSearch();
							}}
							aria-label="Close search"
						>
							<Icon name="close" useMaterialIcons />
						</button>
					</div>
					<div
						className="search-nav-bar__results"
						onClick={event => {
							event.preventDefault();
							event.stopPropagation();
						}}
					>
						<div className="search-nav-bar__header">
							<p>Users</p>
							<p className="result-count">
								{users?.length ?? 0}
								{' of '}
								{userTotalCount}
								{usersHasMore ? '+' : ''}
							</p>
						</div>
						{userRender}
						{users === null && !usersLoading && (
							<div className="search-nav-bar__default-message disabled">
								<p className="icon-left icon-search">
									{searchTooShort ? (
										tooShortMessage
									) : (
										<>
											Name, Email, Phone, Invoice ID
											{appConfigStore.vehicleEnabled &&
												`, ${getPascalCaseMessageByKey('vehicle-tab__rego__label', 'Rego')}`}
										</>
									)}
								</p>
							</div>
						)}
						{users?.length === 0 && !usersLoading && (
							<div className="search-nav-bar__no-result disabled">
								<p>No results</p>
							</div>
						)}
						{usersLoading && (
							<div className="search-nav-bar__searching disabled">
								<div className="loader-container">
									<div className="loading-text dots">Searching</div>
								</div>
							</div>
						)}
						<div className="search-nav-bar__header">
							<p>Bookings</p>
							<p className="result-count">
								{(bookings?.length ?? 0) + (parkingResults?.length ?? 0) + (charterResults?.length ?? 0)}
								{' of '}
								{bookingTotalCount + parkingTotalCount + charterTotalCount}
							</p>
						</div>
						{bookingRender}
						{bookings === null && !bookingsLoading && (
							<div className="search-nav-bar__default-message disabled">
								<p className="icon-left icon-search">
									{searchTooShort ? tooShortMessage : 'Booking ID, Parking ID, Charter ID, Rego'}
								</p>
							</div>
						)}
						{bookings?.length === 0 &&
							(parkingResults?.length ?? 0) === 0 &&
							(charterResults?.length ?? 0) === 0 &&
							!bookingsLoading && (
							<div className="search-nav-bar__no-result disabled">
								<p>No results</p>
							</div>
						)}
						{bookingsLoading && (
							<div className="search-nav-bar__searching disabled">
								<div className="loader-container">
									<div className="loading-text dots">Searching</div>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
